import React, { useState } from 'react';
import { Box, Typography, Alert, Button } from '@mui/material';
import { Download as DownloadIcon, Warning as WarningIcon } from '@mui/icons-material';
import { SOFTWARE_DOWNLOAD_URL } from '../constants/software';

/**
 * Windows installer download UI: SmartScreen guidance, acknowledgement, and download action.
 * Used by the dedicated download page and kept in sync with onboarding copy.
 */
interface DownloadWindowsContentProps {
  onDownloadStarted?: () => void;
}

export default function DownloadWindowsContent({ onDownloadStarted }: DownloadWindowsContentProps) {
  const [downloadAcknowledged, setDownloadAcknowledged] = useState(false);
  const [error, setError] = useState('');

  const handleDownload = () => {
    if (!downloadAcknowledged) {
      setError('Please acknowledge the Windows SmartScreen warning before downloading');
      return;
    }
    setError('');
    const downloadWindow = window.open(SOFTWARE_DOWNLOAD_URL, '_blank');
    if (!downloadWindow) {
      setError('Could not start download. Please allow popups and try again.');
      return;
    }
    onDownloadStarted?.();
  };

  return (
    <Box>
      <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Windows SmartScreen Warning
        </Typography>
        <Typography variant="body2" component="div">
          When you download and run the installer, Windows may show a purple warning screen saying
          &quot;Windows protected your PC&quot; because the app is from an unknown publisher.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>This is normal and safe.</strong> The software is properly code-signed and certified.
          If you click &quot;More info&quot; or &quot;Show more&quot;, you can see that it&apos;s registered to{' '}
          <strong>Illeris AS</strong>.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
          We&apos;re still working on building enough download reputation to remove this warning automatically.
          You&apos;ll need to manually click &quot;Run anyway&quot; to proceed with the installation.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          By clicking the download button below, you acknowledge that:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li>
            <Typography variant="body2" color="text.secondary">
              You understand the Windows SmartScreen warning is expected
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">
              The software is certified and registered to Illeris AS
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">
              You will need to manually accept the risk to proceed with installation
            </Typography>
          </li>
        </Box>
      </Box>

      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={handleDownload}
        disabled={!downloadAcknowledged}
        startIcon={<DownloadIcon />}
        sx={{ mb: 2 }}
      >
        Download TimeSentinel
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <input
          type="checkbox"
          id="download-acknowledge-windows"
          checked={downloadAcknowledged}
          onChange={(e) => {
            setDownloadAcknowledged(e.target.checked);
            if (e.target.checked) setError('');
          }}
          style={{ marginRight: 8 }}
        />
        <label htmlFor="download-acknowledge-windows">
          <Typography variant="body2" component="span">
            I understand and acknowledge the Windows SmartScreen warning
          </Typography>
        </label>
      </Box>
    </Box>
  );
}
